import { useMemo, useRef, useState } from 'react'
import { rememberCreationDocumentIds } from '@/analytics/utils/table-analytics'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { tableUploadRegistered } from '../analysis/store/upload-actions'
import { selectAllFiles, selectUploadFilesByServerFileId } from '@/uppy-file-upload/store/selectors'
import { useUserFiles } from '@/user_files/files/hooks/useUserFiles'
import { useStagedFilesUpload } from '@/matter-asset-common/files/hooks/useStagedFilesUpload'
import { getStagedFileCardState } from '@/matter-asset-common/files/utils/get-staged-file-card-state'
import { kAcceptedFilesList } from '@/constants/constants-gcs'
import { isFileAcceptedForUserFilesUpload } from '@/user_files/utils/file-type-utils'

/** Selection belongs to this creation page; uploads and processing use the shared file pipeline. */
export function useTableCreationFiles(matterId: string, enabled: boolean) {
  const dispatch = useAppDispatch()
  const destinationTableId = useRef<string | null>(null)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [uploadClientIds, setUploadClientIds] = useState<string[]>([])
  // Creation may resolve after more files register. Its completion callback must attach
  // the latest IDs, including registrations that React has not rendered yet.
  const currentUploadIds = useRef(uploadClientIds)
  currentUploadIds.current = uploadClientIds
  const uploads = useAppSelector(selectAllFiles)
  const uploadsByFileId = useAppSelector(selectUploadFilesByServerFileId)
  const documentIds = useMemo(
    () => [
      ...new Set([
        ...selectedIds,
        ...uploadClientIds.flatMap((id) => {
          const fileId = uploads[id]?.client.serverFileId
          return fileId ? [fileId] : []
        }),
      ]),
    ],
    [selectedIds, uploadClientIds, uploads]
  )
  const pendingUploadClientIds = uploadClientIds.filter((id) => !uploads[id]?.client.serverFileId)
  const { filesById, error } = useUserFiles(documentIds)
  const upload = useStagedFilesUpload({
    enabled,
    matterId,
    folderName: 'Tables',
    acceptedFileTypes: kAcceptedFilesList,
    isFileAccepted: isFileAcceptedForUserFilesUpload,
    sourceId: `table-creation-${matterId}`,
    stagedUserFileIds: documentIds,
    pendingUploadClientIds,
    onFileRegistered: (id) => {
      currentUploadIds.current = [...currentUploadIds.current, id]
      setUploadClientIds(currentUploadIds.current)
      if (destinationTableId.current) dispatch(tableUploadRegistered({ matterId, tableId: destinationTableId.current, clientTmpId: id }))
    },
  })
  const cards = [
    ...documentIds.map((fileId) => ({
      id: fileId,
      ...getStagedFileCardState({
        fileId,
        file: filesById[fileId],
        uploadState: uploadsByFileId[fileId],
        // A completed upload already has a valid server identity; metadata can arrive through polling.
        loadError: Boolean(error && !filesById[fileId] && uploadsByFileId[fileId]?.client.uploadStatus !== 'uploaded'),
      }),
    })),
    ...pendingUploadClientIds.map((id) => ({ id, ...getStagedFileCardState({ uploadState: uploads[id] }) })),
  ]
  const remove = (id: string) => {
    const removedUploads = uploadClientIds.filter((clientId) => clientId === id || uploads[clientId]?.client.serverFileId === id)
    removedUploads.forEach((clientId) => upload.cancelUpload(clientId))
    setUploadClientIds((ids) => ids.filter((clientId) => !removedUploads.includes(clientId)))
    setSelectedIds((ids) => ids.filter((fileId) => fileId !== id))
  }
  const selectFiles = (ids: string[]) => {
    const removedUploads = uploadClientIds.filter((clientId) => {
      const fileId = uploads[clientId]?.client.serverFileId
      return fileId && !ids.includes(fileId)
    })
    removedUploads.forEach((clientId) => upload.cancelUpload(clientId))
    setUploadClientIds((clientIds) => clientIds.filter((id) => !removedUploads.includes(id)))
    setSelectedIds(ids)
  }
  const clear = () => {
    uploadClientIds.forEach((clientId) => upload.cancelUpload(clientId))
    setUploadClientIds([])
    setSelectedIds([])
  }
  const attachUploadsToTable = (tableId: string, countedDocumentIds: readonly string[]) => {
    destinationTableId.current = tableId
    rememberCreationDocumentIds(tableId, currentUploadIds.current, countedDocumentIds)
    currentUploadIds.current.forEach((clientTmpId) => dispatch(tableUploadRegistered({ matterId, tableId, clientTmpId })))
  }
  return { upload, documentIds, cards, selectFiles, remove, clear, attachUploadsToTable }
}
